import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardList,
  Edit3,
  Link,
  Send,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { toast } from '@clareefai/ui';
import type { InvitationStatus, RecipientType } from '@clareefai/types';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import {
  useGetSurveyQuery,
  useGetSurveyInvitationsQuery,
  useCopySurveyLinkMutation,
  useDeleteSurveyMutation,
} from '../../store/surveyApi';

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  DRAFT: 'bg-gray-100 text-gray-600',
  CLOSED: 'bg-red-100 text-red-700',
};

const INVITATION_STATUS_COLORS: Record<InvitationStatus, string> = {
  COMPLETED: 'bg-green-100 text-green-700',
  SENT: 'bg-blue-100 text-blue-700',
  EXPIRED: 'bg-gray-100 text-gray-500',
};

const RECIPIENT_TYPE_LABEL_KEYS: Record<RecipientType, string> = {
  PROMOTER: 'surveys.detail.recipientType.PROMOTER',
  CRM_IMPORT: 'surveys.detail.recipientType.CRM_IMPORT',
  CSV_IMPORT: 'surveys.detail.recipientType.CSV_IMPORT',
};

const SurveyDetail: React.FC = () => {
  const { t } = useTranslation();
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();

  const { data: survey, isLoading: surveyLoading } = useGetSurveyQuery(surveyId!, { skip: !surveyId });
  const { data: invitations = [], isLoading: invitationsLoading } = useGetSurveyInvitationsQuery(surveyId!, { skip: !surveyId });
  const [copyLink, { isLoading: copyingLink }] = useCopySurveyLinkMutation();
  const [deleteSurvey, { isLoading: deletingLoading }] = useDeleteSurveyMutation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDelete = async () => {
    if (!surveyId) return;
    try {
      await deleteSurvey(surveyId).unwrap();
      toast.success(t('surveys.surveyDeleted'));
      navigate('/survey');
    } catch {
      toast.error(t('surveys.failedToDelete'));
    }
  };

  const handleCopyLink = async () => {
    if (!surveyId) return;
    try {
      const result = await copyLink(surveyId).unwrap();
      await navigator.clipboard.writeText(result.link);
      toast.success(t('surveys.surveyLinkCopied'));
    } catch {
      toast.error(t('surveys.failedToCopyLink'));
    }
  };

  if (surveyLoading) {
    return (
      <div className="space-y-6 max-w-4xl">
        <div className="h-8 w-64 bg-gray-100 rounded animate-pulse" />
        <div className="h-48 bg-gray-100 rounded-xl animate-pulse" />
        <div className="h-64 bg-gray-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="text-center py-20">
        <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500 font-medium">{t('surveys.detail.notFound')}</p>
        <button onClick={() => navigate('/survey')} className="mt-4 text-sm text-purple-600 hover:underline">
          {t('surveys.detail.backToSurveys')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/survey')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors mt-0.5">
          <ArrowLeft className="w-4 h-4 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900">{survey.title}</h1>
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${STATUS_COLORS[survey.status] ?? 'bg-gray-100 text-gray-600'}`}>
              {survey.status}
            </span>
          </div>
          {survey.description && <p className="text-sm text-gray-500 mt-1">{survey.description}</p>}
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-400">{t('surveys.columns.questions')}</p>
          <p className="text-lg font-bold text-gray-900">{survey.questions.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-400">{t('surveys.columns.responses')}</p>
          <p className="text-lg font-bold text-gray-900">{survey.responsesCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-400">{t('surveys.detail.invitations')}</p>
          <p className="text-lg font-bold text-gray-900">{invitations.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-400">{t('surveys.detail.createdLabel')}</p>
          <p className="text-sm font-semibold text-gray-700 mt-0.5">
            {new Date(survey.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => navigate(`/survey/questions/${surveyId}`)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Edit3 className="w-4 h-4" />
          {t('surveys.detail.editQuestions')}
        </button>
        <button
          onClick={() => navigate(`/survey/send/${surveyId}`)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Send className="w-4 h-4" />
          {t('surveys.detail.sendSurvey')}
        </button>
        <button
          onClick={handleCopyLink}
          disabled={copyingLink}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <Link className="w-4 h-4" />
          {t('surveys.copyLink')}
        </button>
        <button
          onClick={() => setShowDeleteDialog(true)}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          {t('surveys.delete')}
        </button>
      </div>

      {/* Participants table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{t('surveys.detail.participantsTitle')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {invitations.length} {invitations.length === 1 ? t('surveys.detail.invitationSent') : t('surveys.detail.invitationsSent')}
            </p>
          </div>
          {invitations.length > 0 && (
            <button
              onClick={() => navigate(`/survey/send/${surveyId}`)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
            >
              <UserPlus className="w-3.5 h-3.5" />
              {t('surveys.detail.addMoreParticipants')}
            </button>
          )}
        </div>

        {invitationsLoading ? (
          <div className="divide-y divide-gray-100">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="px-6 py-4 flex items-center gap-4">
                <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-48 bg-gray-100 rounded animate-pulse" />
                <div className="h-4 w-16 bg-gray-100 rounded animate-pulse ml-auto" />
              </div>
            ))}
          </div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-14">
            <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 font-medium">{t('surveys.detail.noParticipantsYet')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('surveys.detail.noParticipantsHelp')}</p>
            <button
              onClick={() => navigate(`/survey/send/${surveyId}`)}
              className="mt-4 flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors mx-auto"
            >
              <Send className="w-4 h-4" />
              {t('surveys.detail.inviteParticipants')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('surveys.detail.columns.name')}</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('surveys.detail.columns.email')}</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('surveys.detail.columns.type')}</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('surveys.detail.columns.status')}</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('surveys.detail.columns.sent')}</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('surveys.detail.columns.completed')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invitations.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">{inv.recipientName}</td>
                    <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">{inv.recipientEmail}</td>
                    <td className="px-6 py-3">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {RECIPIENT_TYPE_LABEL_KEYS[inv.recipientType] ? t(RECIPIENT_TYPE_LABEL_KEYS[inv.recipientType]) : inv.recipientType}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${INVITATION_STATUS_COLORS[inv.status]}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {new Date(inv.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {inv.completedAt
                        ? new Date(inv.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('surveys.deleteSurveyTitle')}
        description={t('surveys.deleteSurveyDesc')}
        confirmLabel={t('surveys.deleteButton')}
        variant="danger"
        isLoading={deletingLoading}
      />
    </div>
  );
};

export default SurveyDetail;
